"use client";

import { useState, useEffect, useCallback } from "react";
import { deleteInfo, getPublicInfo, postInfo, putInfo } from "utils/api";
import { extractErrorMessage, PaginatedApiResponse, Severity } from "utils/helper/ApiResponses";
import { Motto } from "utils/types/Info.types";


interface SnackbarState {
  open: boolean;
  message: string;
  severity: Severity;
}

export function useMottoManager() {
  const [mottos, setMottos] = useState<Motto[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  const [openDialog, setOpenDialog] = useState(false);
  const [currentMotto, setCurrentMotto] = useState<Motto | null>(null);

  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: "",
    severity: "success",
  });

  // --- Snackbar Handlers ---
  const handleSnackbarOpen = useCallback((message: string, severity: Severity) => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // --- Data Fetching ---
  const fetchMottos = useCallback(async () => {
    setLoading(true);
    try {
      // Use the new getPublicInfo service for the 'mottos' tab
      const result = await getPublicInfo<Motto>(['mottos'], {
        page: 1,
        limit: 100, // Fetch all
        sortBy: 'motto',
        order: 'asc',
      });
      
      const mottosList = (result.data as PaginatedApiResponse<Motto>).data;
      setMottos(mottosList || []);
      
    } catch (err: any) {
      console.error("Error fetching mottos:", err);
      handleSnackbarOpen(extractErrorMessage(err).message, "error");
    } finally {
      setLoading(false);
    }
  }, [handleSnackbarOpen]);

  // Fetch mottos when the component mounts
  useEffect(() => {
    fetchMottos();
  }, [fetchMottos]);

  // --- CRUD Operations ---
  const handleSaveMotto = async (motto: Omit<Motto, 'id'> & { id?: string }) => {
    setIsSaving(true);
    try {
      if (motto.id) {
        // Update existing motto
        await putInfo(['mottos'], motto);
        handleSnackbarOpen("Motto mis à jour avec succès!", "success");
      } else {
        // Create new motto
        await postInfo(['mottos'], motto);
        handleSnackbarOpen("Motto créé avec succès!", "success");
      }
      fetchMottos(); // Refresh the list
      handleCloseDialog();
    } catch (error: any) {
      handleSnackbarOpen(extractErrorMessage(error).message, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteMotto = async (id: string) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm("Êtes-vous sûr de vouloir supprimer ce motto ?")) return;

    setIsSaving(true);
    try {
      await deleteInfo(['mottos'], id);
      handleSnackbarOpen("Motto supprimé avec succès!", "success");
      fetchMottos(); // Refresh the list
    } catch (error: any) {
      handleSnackbarOpen(extractErrorMessage(error).message, "error");
    } finally {
      setIsSaving(false);
    }
  };

  // --- Dialog Handlers ---
  const handleOpenAddDialog = () => {
    setCurrentMotto(null);
    setOpenDialog(true);
  };
  
  const handleOpenEditDialog = (motto: Motto) => {
    setCurrentMotto(motto);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentMotto(null);
  };

  return {
    mottos,
    loading,
    isSaving,
    openDialog,
    currentMotto,
    snackbar,
    handleSaveMotto,
    handleDeleteMotto,
    handleSnackbarClose,
    handleOpenAddDialog,
    handleOpenEditDialog,
    handleCloseDialog,
  };
}